import * as THREE from 'three';

export type TrafficSnapFn = (pos: THREE.Vector3) => { point: THREE.Vector3; distance: number; direction?: THREE.Vector3; roadWidth?: number } | null;

type TrafficCar = {
  pos: THREE.Vector3;
  vel: THREE.Vector3;
  heading: number;
  speed: number;
  seed: number;
};

export class TrafficSystem {
  private cars: TrafficCar[] = [];
  private body: THREE.InstancedMesh;
  private glass: THREE.InstancedMesh;
  private driver: THREE.InstancedMesh;

  private tmpMat = new THREE.Matrix4();
  private tmpQuat = new THREE.Quaternion();
  private tmpPos = new THREE.Vector3();
  private tmpScale = new THREE.Vector3();

  constructor(
    private scene: THREE.Scene,
    private count: number,
    private snapToRoad?: TrafficSnapFn
  ) {
    const bodyGeo = new THREE.BoxGeometry(1.9, 0.6, 4.0);
    const bodyMat = new THREE.MeshStandardMaterial({
      color: 0x2f313a,
      roughness: 0.35,
      metalness: 0.35
    });

    const glassGeo = new THREE.BoxGeometry(1.7, 0.45, 1.6);
    const glassMat = new THREE.MeshStandardMaterial({
      color: 0x0a0c10,
      roughness: 0.05,
      transparent: true,
      opacity: 0.55
    });

    const driverGeo = new THREE.PlaneGeometry(0.6, 0.8);
    const driverMat = new THREE.MeshBasicMaterial({
      color: 0x0b0b0d,
      transparent: true,
      opacity: 0.65
    });

    this.body = new THREE.InstancedMesh(bodyGeo, bodyMat, count);
    this.glass = new THREE.InstancedMesh(glassGeo, glassMat, count);
    this.driver = new THREE.InstancedMesh(driverGeo, driverMat, count);

    this.body.castShadow = true;
    this.body.receiveShadow = true;

    this.scene.add(this.body);
    this.scene.add(this.glass);
    this.scene.add(this.driver);

    for (let i = 0; i < count; i++) {
      this.cars.push({
        pos: new THREE.Vector3(),
        vel: new THREE.Vector3(),
        heading: 0,
        speed: 6 + Math.random() * 8,
        seed: Math.random() * 9999
      });
    }
  }

  dispose() {
    this.scene.remove(this.body);
    this.scene.remove(this.glass);
    this.scene.remove(this.driver);
    this.body.geometry.dispose();
    (this.body.material as THREE.Material).dispose();
    this.glass.geometry.dispose();
    (this.glass.material as THREE.Material).dispose();
    this.driver.geometry.dispose();
    (this.driver.material as THREE.Material).dispose();
  }

  resetAround(playerPos: THREE.Vector3) {
    for (const c of this.cars) {
      const angle = Math.random() * Math.PI * 2;
      const r = 40 + Math.random() * 120;
      c.pos.set(playerPos.x + Math.sin(angle) * r, 1.3, playerPos.z + Math.cos(angle) * r);
      c.heading = angle + Math.PI;
      c.vel.set(Math.sin(c.heading), 0, Math.cos(c.heading)).multiplyScalar(c.speed);
    }
    this.syncInstances();
  }

  update(dt: number, playerPos: THREE.Vector3) {
    const minR = 50;
    const maxR = 220;

    for (const c of this.cars) {
      let wander =
        (Math.sin((performance.now() * 0.00022) + c.seed) +
          Math.sin((performance.now() * 0.00011) + c.seed * 2)) * 0.18;
      c.vel.set(Math.sin(c.heading), 0, Math.cos(c.heading)).multiplyScalar(c.speed);
      c.pos.addScaledVector(c.vel, dt);

      if (this.snapToRoad) {
        const snap = this.snapToRoad(c.pos);
        if (snap && snap.distance < 10) {
          c.pos.lerp(snap.point, 0.28);
          const roadWidth = snap.roadWidth ?? 6;
          if (snap.distance > roadWidth * 0.55) {
            c.pos.copy(snap.point);
          }
          if (snap.direction) {
            const targetHeading = Math.atan2(snap.direction.x, snap.direction.z);
            const diff = targetHeading - c.heading;
            c.heading += diff * Math.min(1, dt * 4.5);
            wander *= 0.15;
          }
        }
      }

      c.heading += wander * dt;

      const dx = c.pos.x - playerPos.x;
      const dz = c.pos.z - playerPos.z;
      const d = Math.sqrt(dx * dx + dz * dz);

      if (d > maxR) {
        const angle = Math.random() * Math.PI * 2;
        const r = minR + Math.random() * 30;
        c.pos.set(playerPos.x + Math.sin(angle) * r, 1.3, playerPos.z + Math.cos(angle) * r);
        c.heading = angle + Math.PI;
      } else if (d < minR) {
        c.heading += Math.PI * dt * 0.7;
      }
    }

    this.syncInstances();
  }

  private syncInstances() {
    for (let i = 0; i < this.cars.length; i++) {
      const c = this.cars[i];

      this.tmpQuat.setFromAxisAngle(new THREE.Vector3(0, 1, 0), c.heading);

      this.tmpPos.copy(c.pos);
      this.tmpScale.set(1, 1, 1);
      this.tmpMat.compose(this.tmpPos, this.tmpQuat, this.tmpScale);
      this.body.setMatrixAt(i, this.tmpMat);

      this.tmpPos.set(c.pos.x, c.pos.y + 0.55, c.pos.z);
      this.tmpMat.compose(this.tmpPos, this.tmpQuat, this.tmpScale);
      this.glass.setMatrixAt(i, this.tmpMat);

      this.tmpPos.set(
        c.pos.x + Math.sin(c.heading) * 0.15,
        c.pos.y + 0.55,
        c.pos.z + Math.cos(c.heading) * 0.15
      );
      this.tmpMat.compose(this.tmpPos, this.tmpQuat, this.tmpScale);
      this.driver.setMatrixAt(i, this.tmpMat);
    }

    this.body.instanceMatrix.needsUpdate = true;
    this.glass.instanceMatrix.needsUpdate = true;
    this.driver.instanceMatrix.needsUpdate = true;
  }
}
